import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

const BillingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | trialing | failed | expired
  const [info, setInfo] = useState(null);
  const polledRef = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (polledRef.current >= 8) { setStatus("expired"); return; }
      polledRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
        } else if (data.payment_status === "no_payment_due" && data.status === "complete") {
          // Subscription checkout with a free trial — no charge yet, but the
          // subscription (and trial) is active. See routes/stripe_billing.py.
          setStatus("trialing");
          refresh();
        } else if (data.status === "expired") {
          setStatus("expired");
        } else {
          setTimeout(poll, 2500);
        }
      } catch {
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D1117] px-6">
      <div className="max-w-md w-full border border-gray-900 p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[#002FA7] mx-auto animate-spin" strokeWidth={1.5} />
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4">Confirming payment…</h2>
            <p className="text-sm text-slate-400 mt-2">Hang tight while we verify your transaction.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 bg-[#002FA7] grid place-items-center mx-auto">
              <Check className="w-7 h-7 text-white" strokeWidth={3} />
            </div>
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4" data-testid="payment-success-heading">Payment confirmed.</h2>
            <p className="text-sm text-slate-400 mt-2">
              {info && <>£{((info.amount_total || 0) / 100).toFixed(2)} {info.currency?.toUpperCase()} processed.</>}
            </p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-none mt-6 w-full bg-[#002FA7] hover:bg-[#002480]" data-testid="goto-dashboard-btn">
              Open dashboard
            </Button>
          </>
        )}
        {status === "trialing" && (
          <>
            <div className="w-14 h-14 bg-[#002FA7] grid place-items-center mx-auto">
              <Check className="w-7 h-7 text-white" strokeWidth={3} />
            </div>
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4" data-testid="trial-started-heading">Your free trial has started.</h2>
            <p className="text-sm text-slate-400 mt-2">
              No charge today — you're all set for 7 days. We'll email you 2 days before your card is billed.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-none mt-6 w-full bg-[#002FA7] hover:bg-[#002480]" data-testid="goto-dashboard-btn">
              Open dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-10 h-10 text-red-600 mx-auto" strokeWidth={1.5} />
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4">Payment not completed</h2>
            <p className="text-sm text-slate-400 mt-2">No charge has been made. You can try again from the pricing page.</p>
            <Link to="/pricing">
              <Button variant="outline" className="rounded-none mt-6 w-full border-gray-900" data-testid="retry-pricing-btn">Back to pricing</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
